import React, { useState, useEffect, useRef } from "react";
import { X, ChevronDown, ChevronUp, SlidersHorizontal } from "lucide-react";
import toast from "react-hot-toast";

/**
 * CustomFilter — generic config-driven filter panel (right-side drawer)
 *
 * config.sections: Array of:
 *   { key, label, type: 'checkbox' | 'radio' | 'select' | 'text' | 'dateRange', options?, placeholder? }
 *
 * checkbox / radio options: [{ label, value }]
 * select   options:         [{ label, value }]
 * dateRange:                no options needed — renders From/To on same row
 * text:                     free-text input, optional placeholder
 *
 * validateBeforeApply: (filters) => string | null — error message; drawer stays open
 */
const CustomFilter = ({
  config,
  onApply,
  onReset,
  initialFilters = {},
  open = false,
  onClose,
  validateBeforeApply,
}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [collapsed, setCollapsed] = useState({});
  const drawerRef = useRef(null);

  const appliedSnapshot = JSON.stringify(initialFilters || {});
  useEffect(() => {
    if (open) {
      setFilters(initialFilters || {});
    }
  }, [open, appliedSnapshot]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);

  const toggleSection = (key) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ── value helpers ── */
  const getCheckbox = (key) => filters[key] || [];
  const getRadio = (key) => filters[key] || "";
  const getSelect = (key) => filters[key] || "";
  const getText = (key) => filters[key] || "";
  const getDate = (key, field) => filters[key]?.[field] || "";

  const setCheckbox = (key, value, checked) =>
    setFilters((prev) => {
      const cur = prev[key] || [];
      return {
        ...prev,
        [key]: checked ? [...cur, value] : cur.filter((v) => v !== value),
      };
    });

  const setSimple = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const setDate = (key, field, value) =>
    setFilters((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));

  /* ── active count badge ── */
  const sectionBadge = (section) => {
    const v = filters[section.key];
    if (!v) return null;
    if (Array.isArray(v) && v.length > 0)
      return (
        <span className="ml-auto text-xs bg-indigo-100 text-indigo-600 font-medium px-1.5 py-0.5 rounded-full">
          {v.length}
        </span>
      );
    if (typeof v === "object" && (v.from || v.to))
      return (
        <span className="ml-auto w-2 h-2 rounded-full bg-indigo-500 inline-block" />
      );
    if (typeof v === "string" && v)
      return (
        <span className="ml-auto w-2 h-2 rounded-full bg-indigo-500 inline-block" />
      );
    return null;
  };

  const totalActive = Object.keys(filters).reduce((acc, key) => {
    const v = filters[key];
    if (Array.isArray(v)) return acc + v.length;
    if (typeof v === "object" && v !== null && (v.from || v.to)) return acc + 1;
    if (typeof v === "string" && v) return acc + 1;
    return acc;
  }, 0);

  const handleApply = () => {
    if (typeof validateBeforeApply === "function") {
      const err = validateBeforeApply(filters);
      if (err) {
        toast.error(err);
        return;
      }
    }
    onApply?.(filters);
    onClose?.();
  };

  const handleReset = () => {
    setFilters({});
    onReset?.();
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed top-0 right-0 h-full z-50 flex flex-col bg-white shadow-2xl"
        style={{ width: 380, marginTop: 0 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-gray-500" />
            <span className="text-base font-semibold text-gray-900">
              Filters
            </span>
            {totalActive > 0 && (
              <span className="text-xs bg-indigo-600 text-white font-medium px-2 py-0.5 rounded-full">
                {totalActive}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Sections */}
        <div className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
          {(config?.sections || []).map((section) => {
            const isOpen = !collapsed[section.key];
            return (
              <div
                key={section.key}
                className="border border-gray-100 rounded-lg overflow-hidden"
              >
                {/* Section header */}
                <button
                  onClick={() => toggleSection(section.key)}
                  className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                >
                  <span className="text-sm font-medium text-gray-800 flex-1">
                    {section.label}
                  </span>
                  {sectionBadge(section)}
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                </button>

                {/* Section content */}
                {isOpen && (
                  <div className="px-4 py-3 bg-white">
                    {/* CHECKBOX */}
                    {["checkbox", "dropdown", "user"].includes(
                      section.type,
                    ) && (
                      <div
                        className="space-y-2"
                        style={{ maxHeight: "200px", overflow: "scroll" }}
                      >
                        {(section.options || []).map((opt) => {
                          const checked = getCheckbox(section.key).includes(
                            opt.value,
                          );
                          return (
                            <label
                              key={opt.value}
                              className="flex items-center gap-3 cursor-pointer group"
                            >
                              <div
                                className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                  checked
                                    ? "bg-indigo-600 border-indigo-600"
                                    : "border-gray-300 group-hover:border-indigo-400"
                                }`}
                                onClick={() =>
                                  setCheckbox(section.key, opt.value, !checked)
                                }
                              >
                                {checked && (
                                  <svg
                                    className="w-2.5 h-2.5 text-white"
                                    fill="none"
                                    viewBox="0 0 10 8"
                                  >
                                    <path
                                      d="M1 4l3 3 5-6"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </div>
                              <span
                                className="text-sm text-gray-700"
                                onClick={() =>
                                  setCheckbox(section.key, opt.value, !checked)
                                }
                              >
                                {opt.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {/* RADIO */}
                    {section.type === "radio" && (
                      <div className="space-y-2">
                        {(section.options || []).map((opt) => {
                          const selected = getRadio(section.key) === opt.value;
                          return (
                            <label
                              key={opt.value}
                              className="flex items-center gap-3 cursor-pointer group"
                              onClick={() => setSimple(section.key, opt.value)}
                            >
                              <div
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                  selected
                                    ? "border-indigo-600"
                                    : "border-gray-300 group-hover:border-indigo-400"
                                }`}
                              >
                                {selected && (
                                  <div className="w-2 h-2 rounded-full bg-indigo-600" />
                                )}
                              </div>
                              <span className="text-sm text-gray-700">
                                {opt.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {/* SELECT */}
                    {section.type === "select" && (
                      <select
                        value={getSelect(section.key)}
                        onChange={(e) => setSimple(section.key, e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="">
                          {section.placeholder || "Select..."}
                        </option>
                        {(section.options || []).map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {/* TEXT */}
                    {section.type === "text" && (
                      <input
                        type="text"
                        value={getText(section.key)}
                        onChange={(e) => setSimple(section.key, e.target.value)}
                        placeholder={
                          section.placeholder || `Filter by ${section.label}...`
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    )}

                    {/* DATE RANGE — From and To on same row */}
                    {section.type === "date_range" && (
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">
                            From
                          </label>
                          <input
                            type="date"
                            value={getDate(section.key, "from")}
                            onChange={(e) =>
                              setDate(section.key, "from", e.target.value)
                            }
                            className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                        </div>
                        <div className="flex-shrink-0 mt-4 text-gray-400 text-sm">
                          —
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">
                            To
                          </label>
                          <input
                            type="date"
                            value={getDate(section.key, "to")}
                            onChange={(e) =>
                              setDate(section.key, "to", e.target.value)
                            }
                            className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-2 py-4 border-t border-gray-200 bg-gray-50 flex gap-3">
          <button
            onClick={handleReset}
            className="flex-1 h-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={handleApply}
            className="flex-1 h-10 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
};

export default CustomFilter;
